'use client'
import type { Address } from '@morpho-org/blue-sdk'
import type { CSSProperties, ReactNode } from 'react'

import { ActionButtons } from '@/components/common/ActionButtons'
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout'
import { BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages'
import { TextError } from '@/components/common/Error'
import { TransferError } from '@/components/common/Error/TransferErrors'

/**
 * One "Error fetching …" line, right-aligned. Renders nothing when the error is
 * absent, so callers can list every field error unconditionally.
 */
export const VaultV1FieldError = ({
  error,
  message
}: {
  error?: { message: string } | null
  message: string
}) =>
  error ? (
    <div className="flex justify-end">
      <TextError message={message} messageTooltip={error.message} />
    </div>
  ) : null

interface VaultV1OperationShellProps {
  /** Asset input variant (`LargeAssetInput` on the panel, `CompactAssetInput` in the PM dialog). */
  assetInput: ReactNode
  /** `VaultV1FieldError` lines for this operation (deposit has three, withdraw one). */
  fieldErrors?: ReactNode
  /** "Pay with" segment for native wrapping, deposit-only. */
  wrappingControl?: ReactNode
  /** Summary slot (`VaultV1Summary` on the panel, `VaultV1TransactionInfoCard` in the PM dialog). */
  summary: ReactNode
  /** Vault asset address, used to render the permissioned-transfer error. */
  assetAddress?: Address
  canTransfer: boolean
  /** The issuer hasn't whitelisted the bundle contract for the vault asset — blocks the CTA. */
  isBundleNotAuthorized?: boolean
  /** Callout copy when `isBundleNotAuthorized` (defaults to the deposit message). */
  bundleNotAuthorizedMessage?: string
  handleFinalize: () => void
  isLoading: boolean
  /** Default action label when nothing disables the button (e.g. "Review Deposit"). */
  reviewLabel: string
  disabledLabel?: string | null
  tooltipMessage?: { message: string | undefined; textStyle: CSSProperties }
}

/**
 * Shared deposit/withdraw body for the Vault V1 simple action panel and the
 * position-manager dialogs. The operation logic and the review-dialog opener
 * live in each caller; this owns only the duplicated presentational shell:
 * asset input + field errors + the optional "Pay with" segment + transfer error,
 * the summary slot, and the action button with its tooltip.
 */
const VaultV1OperationShell = ({
  assetInput,
  fieldErrors,
  wrappingControl,
  summary,
  assetAddress,
  canTransfer,
  isBundleNotAuthorized = false,
  bundleNotAuthorizedMessage = BUNDLE_NOT_AUTHORIZED_MESSAGE,
  handleFinalize,
  isLoading,
  reviewLabel,
  disabledLabel,
  tooltipMessage
}: VaultV1OperationShellProps) => (
  <>
    <div className="gap-2xs flex flex-col">
      {assetInput}
      {fieldErrors}
      {wrappingControl}
      {!canTransfer && assetAddress && <TransferError tokenAddress={assetAddress} />}
    </div>

    {summary}

    {isBundleNotAuthorized && (
      <BundleAuthorizationCallout variant="destructive" message={bundleNotAuthorizedMessage} />
    )}

    <ActionButtons
      handleFinalize={handleFinalize}
      isLoading={isLoading}
      finalizeLabel={disabledLabel ?? reviewLabel}
      tooltipContent={
        tooltipMessage ? (
          <span className="text-3xs" style={tooltipMessage.textStyle}>
            {tooltipMessage.message}
          </span>
        ) : undefined
      }
      isFinalizeDisabled={!!disabledLabel || !canTransfer || isBundleNotAuthorized}
    />
  </>
)

export default VaultV1OperationShell
